import PropTypes from "prop-types";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  LoadingContext,
  M_StateActions,
  MTable,
  NavigateContext,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Carrier Login CRUD page in a tab.
 */
const CARRIER_LOGIN_CRUD_ROUTE = "/home/carriers/carrierLoginsConfigCrud";

/**
 * Width of the side tabs rail used by this route layout.
 *
 * Purpose:
 * - keeps MTable aligned with pages rendered beside the tabs rail;
 * - preserves the previous page behavior without inline layout wrappers.
 */
const TABS_RAIL_WIDTH = 250;

//endregion

//region Component

/**
 * Renders the Carrier Logins configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the CarrierLogins MTable;
 * - use the Carriers permission module so existing permissions continue to apply;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - keep the selected tab title synchronized with the active language.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Carrier Logins configuration page.
 */
export default function CarrierLoginsConfigPage({
  roles,
  onCreateTab,
  toastMessages,
}) {
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [refresh, setRefresh] = useState(false);

  const selectedTab = useSelector(
    (state) =>
      state.StateKeyValue.find((obj) => obj.key === "selectedTab")?.value
  );

  /**
   * Removes table actions that are not required in this configuration page.
   *
   * Important:
   * - Print is not part of the desired Carrier Logins workflow;
   * - Copy is disabled to keep login creation explicit.
   */
  const removeContextItems = useMemo(() => ["Print", "Copy"], []);

  /**
   * Keeps the current tab label aligned with the active language.
   *
   * Important:
   * - the key matches the MenuDisplay value used by the backend seed;
   * - this keeps the tab caption correct after runtime language changes.
   */
  useEffect(() => {
    if (!selectedTab) return;

    renameTab(String(selectedTab), t("MenuCarrierLoginManagementLabel"));
  }, [renameTab, selectedTab, t]);

  /**
   * Opens the Carrier Login CRUD tab for the requested operation.
   *
   * Flow:
   * - persists the operation in M_State;
   * - validates row selection for View/Edit/Copy;
   * - persists the selected carrier login row when required;
   * - opens the configured CRUD route with a contextual tab label.
   *
   * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
   * @param {object|null|undefined} row - Selected carrier login row.
   * @returns {void}
   */
  const openCarrierLoginCrud = useCallback(
    (operation, row) => {
      dispatch(
        M_StateActions.addM_State({
          key: "operation",
          value: operation,
        })
      );

      if (["Edit", "Copy", "View"].includes(operation)) {
        if (!row) {
          toastMessages("error", "", t("SelectedLine"));
          return;
        }

        dispatch(
          M_StateActions.addM_State({
            key: "CarrierLoginLine",
            value: row,
          })
        );
      }

      const safeName = row?.Name ?? "";

      const tabLabel =
        operation === "Create"
          ? t("LabelNewCarrierLogin")
          : operation === "View"
            ? `${t("LabelViewCarrierLogin")} "${safeName}"`
            : `${t("LabelEditCarrierLogin")} "${safeName}"`;

      onCreateTab(CARRIER_LOGIN_CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  /**
   * Replaces MTable default CRUD handlers with the tab-based Carrier Login workflow.
   */
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ["View", (row) => openCarrierLoginCrud("View", row)],
        ["Create", () => openCarrierLoginCrud("Create")],
        ["Edit", (row) => openCarrierLoginCrud("Edit", row)],
      ]),
    [openCarrierLoginCrud]
  );

  return (
    <div
      className="config-page"
      style={{ "--config-page-title-color": ctx.secondaryColor }}
    >
      <div className="config-page__header">
        <div className="config-page__title">
          {t("MenuCarrierLoginManagementLabel")}
        </div>

        <div className="config-page__description">
          {t("InfoCarrierLogins")}
        </div>
      </div>

      <div className="config-page__table">
        <MTable
          filtersOnTable
          entityName="CarrierLogins"
          roleName="Carriers"
          rolePermissions={roles?.map((role) => role.Value)}
          removeContextItems={removeContextItems}
          refresh={refresh}
          setRefresh={setRefresh}
          useSessionStorage={false}
          replaceFunctions={replaceFunctions}
          tabsRailWidth={TABS_RAIL_WIDTH}
        />
      </div>
    </div>
  );
}

//endregion

//region PropTypes

CarrierLoginsConfigPage.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};

//endregion